import {
  searchContextItems,
  defineFilter,
  defineSearchFilters,
  getSearchOperatorValue
} from '@gat-ship/search'
import type { StakeholderType, GatshipSearchOperator } from '@gat-ship/types'
import type {
  GatshipSearchField,
  GatshipSearchFieldValue,
  SearchContextType,
  SearchFieldModelValue
} from '@gat-ship/search'
import type { IconValues } from '@gat-ship/icons'

export const stakeholderSearchFilterActiveStates = {
  active: 'active',
  inactive: 'inactive',
  all: 'all'
} as const

export interface StakeholderSearchFilterActiveStateMetadata {
  title: string
}

export const activeStateMetadata: Record<
  StakeholderSearchFilterActiveState,
  StakeholderSearchFilterActiveStateMetadata
> = {
  active: {
    title: 'Only active'
  },
  inactive: {
    title: 'Only inactive'
  },
  all: {
    title: 'All active and inactive'
  }
}

const clientSearchFields = {
  name: {
    field: 'name',
    type: 'string',
    operators: ['equals', 'contains']
  } satisfies GatshipSearchField<'name', 'string'>,
  email: {
    field: 'email',
    type: 'string',
    operators: ['equals', 'contains']
  } satisfies GatshipSearchField<'email', 'string'>,
  phone: {
    field: 'phone',
    type: 'string',
    operators: ['equals', 'contains']
  } satisfies GatshipSearchField<'phone', 'string'>,
  state: {
    field: 'state',
    type: 'string',
    operators: ['equals', 'contains']
  } satisfies GatshipSearchField<'state', 'string'>,
  clientNumber: {
    field: 'clientNumber',
    type: 'number',
    operators: ['equals']
  } satisfies GatshipSearchField<'clientNumber', 'number'>
} as const

export type ClientSearchFields = Partial<
  Record<
    keyof typeof clientSearchFields,
    GatshipSearchFieldValue<
      keyof typeof clientSearchFields,
      (typeof clientSearchFields)[keyof typeof clientSearchFields]['type']
    >
  >
>

export type StakeholderSearchFilterActiveState =
  (typeof stakeholderSearchFilterActiveStates)[keyof typeof stakeholderSearchFilterActiveStates]

export function getStakeholderTypeForSearchContext(
  searchContext: SearchContextType
): StakeholderType {
  switch (searchContext) {
    case searchContextItems.client:
      return 'client'
    case searchContextItems.vendors:
      return 'vendor'
    case searchContextItems.otherParties:
      return 'other'
    default:
      throw new Error('Invalid search context.')
  }
}

function constructQuery(
  fieldName: string,
  operator: GatshipSearchOperator,
  fieldValue: string | undefined,
  options?: {
    quotedValue?: boolean
  }
) {
  if (!fieldValue) {
    return ''
  }
  const quotedValue = options?.quotedValue ?? true
  const value = quotedValue ? `"${fieldValue}"` : fieldValue
  return `${fieldName}:${getSearchOperatorValue(operator)}${value}`
}

export function buildStakeholderFilterQuery(
  filters: ReturnType<typeof useStakeholderSearchFilters>
) {
  const typeField = constructQuery(
    filters.type.fieldName,
    filters.type.operator.value,
    filters.type.filterValue.value
  )
  const statusField = constructQuery(
    filters.state.fieldName,
    filters.state.operator.value,
    filters.state.filterValue.value
  )
  if (
    filters.type.filterValue.value === 'client' &&
    filters.clientNumber.filterValue.value
  ) {
    const clientNumberField = constructQuery(
      filters.clientNumber.fieldName,
      filters.clientNumber.operator.value,
      filters.clientNumber.filterValue.value
    )
    return `${clientNumberField} ${typeField} ${statusField}`
  }
  if (
    filters.type.filterValue.value === 'vendor' &&
    filters.vendorNumber.filterValue.value
  ) {
    const vendorNumberField = constructQuery(
      filters.vendorNumber.fieldName,
      filters.vendorNumber.operator.value,
      filters.vendorNumber.filterValue.value
    )
    return `${vendorNumberField} ${typeField} ${statusField}`
  }
  const query: string[] = []
  Object.entries(filters)
    .filter(([key]) => key !== 'state')
    .forEach(([key, value]) => {
      query.push(
        constructQuery(key, value.operator.value, value.filterValue.value)
      )
    })

  const stateField = constructQuery(
    filters.state.fieldName,
    filters.state.operator.value,
    filters.state.filterValue.value === 'any'
      ? '(\"active\" or \"inactive\")'
      : filters.state.filterValue.value,
    {
      quotedValue: filters.state.filterValue.value !== 'any'
    }
  )
  query.push(stateField)
  return query.filter(Boolean).join(' ')
}

export interface CurrentSearch {
  searchTerm?: string
  activeFilters: Record<
    string,
    {
      filterValue: string | undefined
      operator: GatshipSearchOperator
      fieldName: SearchFieldModelValue['fieldName']
    }
  >
}

export function getCurrentSearchFromFilters(
  filters: ReturnType<typeof useStakeholderSearchFilters>
): CurrentSearch {
  const result: CurrentSearch = {
    activeFilters: {}
  }

  const stateField = {
    filterValue: filters.state.filterValue.value,
    operator: filters.state.operator.value,
    fieldName: filters.state.fieldName
  }
  const typeField = {
    filterValue: filters.type.filterValue.value,
    operator: filters.type.operator.value,
    fieldName: filters.type.fieldName
  }
  if (
    filters.type.filterValue.value === 'client' &&
    filters.clientNumber.filterValue.value
  ) {
    result.activeFilters.type = typeField
    result.activeFilters.state = stateField
    result.activeFilters.clientNumber = {
      filterValue: filters.clientNumber.filterValue.value,
      operator: filters.clientNumber.operator.value,
      fieldName: filters.clientNumber.fieldName
    }
    return result
  }
  if (
    filters.type.filterValue.value === 'vendor' &&
    filters.vendorNumber.filterValue.value
  ) {
    result.activeFilters.type = typeField
    result.activeFilters.state = stateField
    result.activeFilters.vendorNumber = {
      filterValue: filters.vendorNumber.filterValue.value,
      operator: filters.vendorNumber.operator.value,
      fieldName: filters.vendorNumber.fieldName
    }
    return result
  }
  Object.entries(filters)
    .filter(([key, value]) => value.filterValue.value)
    .forEach(([key, value]) => {
      result.activeFilters[key] = {
        filterValue: value.filterValue.value,
        operator: value.operator.value,
        fieldName: value.fieldName
      }
    })
  return result
}

export function useStakeholderSearchFilters() {
  return defineSearchFilters({
    type: defineFilter({
      fieldName: 'type',
      defaultOperator: 'equals',
      defaultFilterValue: 'client' satisfies StakeholderType,
      allowedOperators: {
        equals: true
      }
    }),
    name: defineFilter({
      fieldName: 'name',
      defaultOperator: 'contains',
      allowedOperators: {
        contains: true,
        equals: true,
        like: true
      }
    }),
    email: defineFilter({
      fieldName: 'email',
      defaultOperator: 'contains',
      allowedOperators: {
        contains: true,
        equals: true
      }
    }),
    phone: defineFilter({
      fieldName: 'phone',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),
    state: defineFilter({
      fieldName: 'state',
      defaultOperator: 'equals',
      defaultFilterValue: 'active' satisfies StakeholderSearchFilterActiveState,
      allowedOperators: {
        equals: true
      }
    }),
    country: defineFilter({
      fieldName: 'country',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),

    /** Only for vendor type */
    vendorNumber: defineFilter({
      fieldName: 'vendorNumber',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),

    /** Only for client type */
    clientNumber: defineFilter({
      fieldName: 'clientNumber',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    })
  })
}

export type StakeholderSearchFilterKeys = keyof ReturnType<
  typeof useStakeholderSearchFilters
>

/** A mapping of filter field names to their metadata. */
export const filterFieldMetadata: Record<
  StakeholderSearchFilterKeys,
  {
    title: string
    icon: IconValues
  }
> = {
  type: {
    title: 'Type',
    icon: 'User'
  },
  name: {
    title: 'Name',
    icon: 'User'
  },
  email: {
    title: 'Email',
    icon: 'Mail'
  },
  phone: {
    title: 'Phone',
    icon: 'Phone'
  },
  state: {
    title: 'Status',
    icon: 'Flag'
  },
  country: {
    title: 'Country',
    icon: 'Globe'
  },
  vendorNumber: {
    title: 'Vendor Number',
    icon: 'Hash'
  },
  clientNumber: {
    title: 'Client Number',
    icon: 'Hash'
  }
}
